/**
 * useAllProductsPagination Hook
 * Handles server-side cursor-based pagination for the "All Products" tab.
 * Uses Firestore startAfter() for efficient pagination.
 */
import { useState, useCallback, useRef } from 'react';
import { db } from 'config/firebase';
import {
    collection,
    query,
    getDocs,
    orderBy,
    limit,
    startAfter,
    getCountFromServer,
    collectionGroup,
    where
} from 'firebase/firestore';

const PAGE_SIZE = 100;

const useAllProductsPagination = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [error, setError] = useState(null);

    // Store cursors (last doc of each page) for navigation
    const cursorsRef = useRef(new Map()); // Map<pageNumber, lastDocSnapshot>
    const firstDocRef = useRef(null);

    /**
     * Fetch total count of products (variants collection group)
     */
    const fetchTotalCount = useCallback(async () => {
        console.log('[Pagination] Fetching total count...');
        try {
            const countQuery = query(collectionGroup(db, 'variants'));
            const snapshot = await getCountFromServer(countQuery);
            const count = snapshot.data().count;
            console.log(`[Pagination] Total products: ${count}`);
            setTotalProducts(count);
            return count;
        } catch (err) {
            console.error('[Pagination] Error fetching count:', err);
            return 0;
        }
    }, []);

    /**
     * Fetch a specific page of products
     */
    const fetchPage = useCallback(async (pageNum, afterCursor = null) => {
        setLoading(true);
        setError(null);

        console.log(`[Pagination] Fetching page ${pageNum}...`);

        try {
            let q;
            let snapshot;

            // Order by SKU - all variants have this field
            // Note: timestamp.created doesn't exist on most variants, so orderBy would return 0 results
            if (pageNum === 1 || !afterCursor) {
                q = query(
                    collectionGroup(db, 'variants'),
                    orderBy('sku'),
                    limit(PAGE_SIZE)
                );
            } else {
                q = query(
                    collectionGroup(db, 'variants'),
                    orderBy('sku'),
                    startAfter(afterCursor),
                    limit(PAGE_SIZE)
                );
            }
            snapshot = await getDocs(q);
            console.log(`[Pagination] Fetched ${snapshot.docs.length} variant docs`);

            // Step 1: Extract variant data and collect unique parentSkus
            const variants = [];
            const parentSkuSet = new Set();

            snapshot.forEach((doc) => {
                const data = doc.data();
                const pathParts = doc.ref.path.split('/');
                const parent_docId = pathParts[3];

                variants.push({
                    id: doc.id,
                    docId: doc.id,
                    parent_docId,
                    ref: doc.ref,
                    ...data
                });

                if (data.parentSku) {
                    parentSkuSet.add(data.parentSku);
                }
            });

            // Step 2: Fetch parent products in chunks of 10 (Firestore 'in' limit)
            const parentSkus = Array.from(parentSkuSet);
            const parentProducts = [];

            if (parentSkus.length > 0) {
                const chunks = [];
                for (let i = 0; i < parentSkus.length; i += 10) {
                    chunks.push(parentSkus.slice(i, i + 10));
                }

                console.log(`[Pagination] Fetching ${parentSkus.length} parent products in ${chunks.length} chunks`);

                const parentPromises = chunks.map(chunk =>
                    getDocs(query(
                        collection(db, 'productCatalog/topicz/products'),
                        where('sku', 'in', chunk)
                    ))
                );

                const parentSnapshots = await Promise.all(parentPromises);
                parentSnapshots.forEach(snap => {
                    snap.forEach(doc => {
                        const data = doc.data();
                        data.docId = doc.id;
                        parentProducts.push(data);
                    });
                });

                console.log(`[Pagination] Fetched ${parentProducts.length} parent products`);
            }

            // Step 3: Merge parent data into variants (including enrichment data)
            let enrichedCount = 0;
            const docs = variants.map(variant => {
                const parent = parentProducts.find(p => p.sku === variant.parentSku);
                if (parent) {
                    // Copy parent properties, prefixing if already exists on variant
                    for (const prop in parent) {
                        if (variant[prop] !== undefined) {
                            variant[`parent_${prop}`] = parent[prop];
                        } else {
                            variant[prop] = parent[prop];
                        }
                    }
                    // Track enrichment data
                    if (parent.enrichment && Object.keys(parent.enrichment).length > 0) {
                        enrichedCount++;
                    }
                }
                return variant;
            });

            console.log(`[Pagination] Merged ${docs.length} products (${enrichedCount} with AI enrichment)`);

            // Debug: Log first product's fields to check structure
            if (docs.length > 0) {
                console.log('[Pagination] Sample product fields:', Object.keys(docs[0]).join(', '));
                console.log('[Pagination] Sample product:', JSON.stringify(docs[0], null, 2));
            }

            // Store first doc of page 1 for reference
            if (pageNum === 1 && snapshot.docs.length > 0) {
                firstDocRef.current = snapshot.docs[0];
            }

            // Store last doc as cursor for next page
            if (snapshot.docs.length > 0) {
                const lastDoc = snapshot.docs[snapshot.docs.length - 1];
                cursorsRef.current.set(pageNum, lastDoc);
            }

            console.log(`[Pagination] Page ${pageNum} loaded with ${docs.length} products`);
            setProducts(docs);
            setCurrentPage(pageNum);
            setLoading(false);

            return docs;
        } catch (err) {
            console.error('[Pagination] Error fetching page:', err);
            setError(err.message);
            setLoading(false);
            return [];
        }
    }, []);

    /**
     * Initialize - fetch count and first page
     */
    const initialize = useCallback(async () => {
        console.log('[Pagination] Initializing...');
        await fetchTotalCount();
        await fetchPage(1);
        console.log('[Pagination] Initialization complete');
    }, [fetchTotalCount, fetchPage]);

    /**
     * Go to next page
     */
    const goToNextPage = useCallback(async () => {
        const totalPages = Math.ceil(totalProducts / PAGE_SIZE);
        if (currentPage >= totalPages) return;

        const cursor = cursorsRef.current.get(currentPage);
        await fetchPage(currentPage + 1, cursor);
    }, [currentPage, totalProducts, fetchPage]);

    /**
     * Go to previous page
     */
    const goToPrevPage = useCallback(async () => {
        if (currentPage <= 1) return;

        const targetPage = currentPage - 1;

        if (targetPage === 1) {
            // Go back to first page
            await fetchPage(1);
        } else {
            // Use stored cursor for the page before target
            const cursor = cursorsRef.current.get(targetPage - 1);
            await fetchPage(targetPage, cursor);
        }
    }, [currentPage, fetchPage]);

    /**
     * Go to specific page (with limitations due to Firestore cursor-based pagination)
     * For pages we've visited, we can use stored cursors
     * For unvisited pages, we need to iterate through
     */
    const goToPage = useCallback(async (pageNum) => {
        if (pageNum < 1) return;
        const totalPages = Math.ceil(totalProducts / PAGE_SIZE);
        if (pageNum > totalPages) return;
        if (pageNum === currentPage) return;

        if (pageNum === 1) {
            await fetchPage(1);
            return;
        }

        // Check if we have the cursor for the page before
        const prevPageCursor = cursorsRef.current.get(pageNum - 1);

        if (prevPageCursor) {
            // We've visited this page before, use stored cursor
            await fetchPage(pageNum, prevPageCursor);
        } else {
            // Need to iterate through pages to get there
            // For simplicity, start from the highest cached page
            let startPage = 1;
            let cursor = null;

            for (let i = pageNum - 1; i >= 1; i--) {
                if (cursorsRef.current.has(i)) {
                    startPage = i + 1;
                    cursor = cursorsRef.current.get(i);
                    break;
                }
            }

            // Iterate through pages to reach target
            setLoading(true);
            for (let p = startPage; p <= pageNum; p++) {
                const docs = await fetchPage(p, cursor);
                if (docs.length > 0 && p < pageNum) {
                    cursor = cursorsRef.current.get(p);
                }
            }
        }
    }, [currentPage, totalProducts, fetchPage]);

    /**
     * Refresh current page
     */
    const refresh = useCallback(async () => {
        cursorsRef.current.clear();
        firstDocRef.current = null;
        await fetchTotalCount();
        await fetchPage(1);
    }, [fetchTotalCount, fetchPage]);

    return {
        products,
        loading,
        currentPage,
        totalProducts,
        totalPages: Math.ceil(totalProducts / PAGE_SIZE),
        error,
        initialize,
        goToNextPage,
        goToPrevPage,
        goToPage,
        refresh
    };
};

export default useAllProductsPagination;
